import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import * as services from "../services/branchApi";
import {
  setBranchList,
  setCurrentBranch,
  setIsLoading,
} from "../slices/companySlice";
import useOptions from "features/masterData/hooks/useOptions";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";


function toParams(params = {}) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    pagination: {
      page: pagination.page,
      size: pagination.pageSize,
    },
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useCompanyBranchList(props) {
  const { t, i18n } = useTranslation();
  const { onView = () => { }, onEdit = () => { }, onCreate = () => { } } = props;
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.company.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const branchList = useSelector((state) => state.company.branchList);
  const [pagination, setPagination] = useState({ page: 0, size: 10 });
  const [sorter, setSorter] = useState([]);
  const {
    sourceProvinceOptions = [],
    sourceDistrictOptions = [],
    sourceSubDistrictOptions = [],
    getProvinceOptions,
    getDistrictOptions,
    getSubDistrictOptions,
    clearDistrictOptions,
    clearSubDistrictOptions,
  } = useOptions();
  function toInitialValues(initialValues = {}) {
    const {
      province = {},
      district = {},
      subdistrict = {},
      ...rest
    } = initialValues;
    const modifiedValues = {
      ...rest,
      branchAddressStatus: initialValues.address != null,
      branchCompensateStatus: initialValues.bankAccountNo != null,
      statusFlag: initialValues.statusFlag === "ACTIVE" ? "ACTIVE" : "INACTIVE",
      provinceId: province?.provinceId,
      districtId: district?.districtId,
      subDistrictId: subdistrict?.subDistrictId,
    };
    return modifiedValues;
  }
  const getBranchList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getBranchList({
          requestId: generateRandomString(),
          companyId: currentCompany,
          ...params.pagination,
          sortBy: params.sortBy,
        });
        dispatch(setBranchList(response.data?.branchListResp || []));
        setPagination({
          ...pagination,
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch]
  );
  const onSearch = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getBranchList({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
          ...params.pagination,
          page: 0,
          size: 10,
          sortBy: params.sortBy,
        });
        dispatch(setBranchList(response.data?.branchListResp || []));
        setPagination({
          ...pagination,
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch]
  );
  const currentBranch = useSelector(
    (state) => state.company.currentBranch || {}
  );
  const initialValues = useMemo(() => {
    return toInitialValues(currentBranch);
  }, [currentBranch]);

  const handleOnChange = useCallback(
    (tablePagination, tableFilters, tableSorter, tableExtra) => {
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: modifiedSorter,
      });
      setPagination({ page: tablePagination.current, size: tablePagination.pageSize });
      // TODO: If sorting or filtering has changed, Change page to 0
      getBranchList(modifiedParams);
    },
    [getBranchList]
  );

  const handleOnView = useCallback(
    (record) => {
      if (record.id) {
        dispatch(setCurrentBranch({ id: record.id }));
        onView();
      }
    },
    [onView, dispatch]
  );

  const handleOnEdit = useCallback(
    (record) => {
      if (record.id) {
        dispatch(setCurrentBranch({ id: record.id }));
        onEdit();
      }
    },
    [onEdit, dispatch]
  );

  const handleOnDelete = useCallback(
    async (record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to delete ?</span>
          : <span>คุณยืนยันการลบใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const values = {
              branchId: record.id,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag: "DELETE",
            };
            await services.deleteBranch(values);
            message.success(t("common.success"))
            getBranchList({ pagination: { page: pagination.page, size: pagination.size } });
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [currentCompany, dispatch, getBranchList, pagination]
  );

  const handleOnCreate = useCallback(() => {
    dispatch(setCurrentBranch({ id: null }));
    onCreate();
  }, [onCreate, dispatch]);

  const onStatus = useCallback(
    async (val, record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          branchId: record.id,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: val ? "ACTIVE" : "INACTIVE",
        };
        await services.deleteBranch(values);
        message.success(t("common.success"))
        getBranchList({ pagination: { page: pagination.page, size: pagination.size } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, getBranchList, pagination]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getBranchList({ pagination: { page: 0, size: 10 } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);
  const provinceOptionsTh = useMemo(() => {
    const modifiedOptions = sourceProvinceOptions.map((opt) => ({
      ...opt,
      label: opt?.provinceName,
      value: opt?.provinceId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceProvinceOptions]);

  const provinceOptionsEn = useMemo(() => {
    const modifiedOptions = sourceProvinceOptions.map((opt) => ({
      ...opt,
      label: opt?.provinceNameEn,
      value: opt?.provinceId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return modifiedOptions;
  }, [sourceProvinceOptions]);

  return {
    isLoading,
    pagination,
    branchList,
    initialValues,
    provinceOptionsTh,
    provinceOptionsEn,
    onView: handleOnView,
    onEdit: handleOnEdit,
    onStatus,
    onSearch,
    onDelete: handleOnDelete,
    onCreate: handleOnCreate,
    onChange: handleOnChange,
  };
}

export default useCompanyBranchList;
